import { Injectable } from '@angular/core';
import moment from 'moment-timezone';
import { LocalizationService } from './localization.service';
import { StudioConfigService } from './studio-config.service';

@Injectable({
  providedIn: 'root'
})
export class ReportDateService {
  private reportDateFormat = 'MM/DD/YYYY';
  private reportDateTimeFormat = 'MM/DD/YYYY h:mm A';
  private reportTimeFormat = 'h:mm A';
  private studioTimezone = 'America/New_York';

  private static sharedInstance: ReportDateService;

  /**
   * One app-wide instance for classes that can't take the service by DI (the
   * BaseComponent constructor chain). Each `new ReportDateService()` subscribes
   * to the app-lifetime studio config and is never released, so constructing one
   * per table/dialog accumulated subscribers (and lost the format cache).
   */
  static shared(studioConfigService: StudioConfigService): ReportDateService {
    if (!ReportDateService.sharedInstance) {
      ReportDateService.sharedInstance = new ReportDateService(studioConfigService, new LocalizationService());
    }
    return ReportDateService.sharedInstance;
  }

  constructor(
    private studioConfigService: StudioConfigService,
    private localizationService: LocalizationService
  ) {
    this.studioConfigService.getStudioConfig().subscribe(config => {
      const countryCode = config?.['field_studio_address']?.[0]?.country_code || 'US';
      const formats = this.localizationService.getDateFormatsForCountry(countryCode);

      this.reportDateFormat = formats.calendarDateFormat;
      this.reportDateTimeFormat = formats.shortDateTimeFormatMoment;
      this.studioTimezone = config?.['field_studio_timezone']?.[0]?.value || 'America/New_York';
      this.formatCache.clear();
    });
  }

  // Templates call formatDate/formatDateTime/formatTime on every change
  // detection pass, and a moment-timezone parse + format is expensive enough
  // that large tables (e.g. hidden student-dashboard tabs) made every click
  // lag. The output depends only on the input, format and studio timezone, so
  // memoize it; cleared whenever the studio config (format/timezone) changes.
  private formatCache = new Map<string, string | null>();
  private static readonly FORMAT_CACHE_LIMIT = 5000;

  private formatCached(value: Date | string | number | null | undefined, format: string): string | null {
    if (typeof value !== 'string' && typeof value !== 'number') {
      const parsed = this.parse(value);
      return parsed ? parsed.format(format) : null;
    }
    // typeof is part of the key: '1700000000' (string, seconds) and
    // 1700000000 (number, milliseconds) parse differently.
    const key = `${typeof value}|${format}|${value}`;
    let formatted = this.formatCache.get(key);
    if (formatted === undefined) {
      const parsed = this.parse(value);
      formatted = parsed ? parsed.format(format) : null;
      if (this.formatCache.size >= ReportDateService.FORMAT_CACHE_LIMIT) {
        this.formatCache.clear();
      }
      this.formatCache.set(key, formatted);
    }
    return formatted;
  }

  /** The studio's calendar date format (moment tokens), e.g. MM/DD/YYYY. */
  calendarDateFormat(): string {
    return this.reportDateFormat;
  }

  formatDate(value: Date | string | number | null | undefined, fallback: string = ''): string {
    return this.formatCached(value, this.reportDateFormat) ?? fallback;
  }

  formatDateTime(value: Date | string | number | null | undefined, fallback: string = ''): string {
    return this.formatCached(value, this.reportDateTimeFormat) ?? fallback;
  }

  formatTime(value: Date | string | number | null | undefined, fallback: string = ''): string {
    return this.formatCached(value, this.reportTimeFormat) ?? fallback;
  }

  formatDateAfterDuration(value: Date | string | number | null | undefined, duration: moment.DurationInputArg1, fallback: string = ''): string {
    const parsed = this.parse(value);
    return parsed ? parsed.clone().add(moment.duration(duration)).format(this.reportDateFormat) : fallback;
  }

  formatTimeAfterDuration(value: Date | string | number | null | undefined, duration: moment.DurationInputArg1, fallback: string = ''): string {
    const parsed = this.parse(value);
    return parsed ? parsed.clone().add(moment.duration(duration)).format(this.reportTimeFormat) : fallback;
  }

  formatCalendarParam(value: Date | string | number | null | undefined, fallback: string = ''): string {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }

    if (value instanceof Date) {
      const parsedDate = moment(value);
      return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : fallback;
    }

    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
      return value.trim();
    }

    if (typeof value === 'number') {
      const parsedTimestamp = moment(value);
      return parsedTimestamp.isValid() ? parsedTimestamp.tz(this.studioTimezone).format('YYYY-MM-DD') : fallback;
    }

    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : fallback;
  }

  currentStudioDateKey(): string {
    return moment().tz(this.studioTimezone).format('YYYY-MM-DD');
  }

  /**
   * The studio's current calendar date as a browser-local Date at start of
   * day, for date-only picker [min] bounds. Resolves "today" in the studio's
   * timezone (field_studio_timezone, like day-view), NOT the client's clock,
   * then re-expresses it as a local Date so the picker's day-cell comparison
   * lands on the right cell for clients browsing from other timezones.
   */
  studioTodayStart(): Date {
    return moment(this.currentStudioDateKey(), 'YYYY-MM-DD', true).startOf('day').toDate();
  }

  /**
   * The studio's current calendar date at end of day, for date-only picker
   * [max] bounds, keeping the studio's today selectable as a range end.
   */
  studioTodayEnd(): Date {
    return moment(this.currentStudioDateKey(), 'YYYY-MM-DD', true).endOf('day').toDate();
  }

  /**
   * Relative-days label for a due date: "113d overdue", "Due today",
   * "Due in 244d"; '' when the value is missing or unparseable. Compares
   * date-only in the studio timezone so a payment isn't "overdue" during the
   * evening of its own due date. `today` is injectable for tests.
   */
  relativeDueLabel(value: Date | string | number | null | undefined, today?: string): string {
    const parsed = this.parse(value);
    if (!parsed) {
      return '';
    }
    const dueDay = moment(parsed.format('YYYY-MM-DD'), 'YYYY-MM-DD', true);
    const todayDay = moment(today ?? this.currentStudioDateKey(), 'YYYY-MM-DD', true);
    if (!dueDay.isValid() || !todayDay.isValid()) {
      return '';
    }
    const days = dueDay.diff(todayDay, 'days');
    if (days < 0) {
      return `${-days}d overdue`;
    }
    if (days === 0) {
      return 'Due today';
    }
    return `Due in ${days}d`;
  }

  formatDateKey(value: Date | string | number | null | undefined, fallback: string = 'unknown-date'): string {
    const parsed = this.parse(value);
    return parsed ? parsed.format('YYYY-MM-DD') : fallback;
  }

  parseCalendarDateInput(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    const parsed = moment(value, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed.toDate() : null;
  }

  private parse(value: Date | string | number | null | undefined): moment.Moment | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (value instanceof Date) {
      const parsedDate = moment(value);
      return parsedDate.isValid() ? parsedDate : null;
    }

    if (typeof value === 'number') {
      const parsedTimestamp = moment(value);
      return parsedTimestamp.isValid() ? parsedTimestamp.tz(this.studioTimezone) : null;
    }

    const trimmed = value.trim();

    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const parsedDateOnly = moment(trimmed, 'YYYY-MM-DD', true);
      return parsedDateOnly.isValid() ? parsedDateOnly : null;
    }

    // Bare slash-format date (no time/zone): treat as a calendar date, not a UTC
    // midnight that gets rotated by studio timezone. PHP backends emit m/d/Y
    // (e.g. report headers like `data['#date_start']`); without an explicit
    // format moment falls back to `new Date(string)`, which parses these as
    // UTC midnight and then `.tz('America/New_York')` rolls the displayed day
    // back by one. Parse strictly here so the date stays put.
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
      const parsedSlash = moment(trimmed, ['MM/DD/YYYY', 'M/D/YYYY'], true);
      return parsedSlash.isValid() ? parsedSlash : null;
    }

    if (/^\d+$/.test(trimmed)) {
      const parsedTimestamp = moment(parseInt(trimmed, 10) * 1000);
      return parsedTimestamp.isValid() ? parsedTimestamp.tz(this.studioTimezone) : null;
    }

    if (/[zZ]|[+\-]\d{2}:?\d{2}$/.test(trimmed)) {
      const zonedDate = moment.parseZone(trimmed);
      return zonedDate.isValid() ? zonedDate.tz(this.studioTimezone) : null;
    }

    const parsedStudioDateTime = moment.tz(trimmed, this.studioTimezone);
    if (parsedStudioDateTime.isValid()) {
      return parsedStudioDateTime;
    }

    const parsedFallback = moment(trimmed);
    return parsedFallback.isValid() ? parsedFallback.tz(this.studioTimezone) : null;
  }
}
